import React, { useMemo, useState } from 'react';
import { AlertCircle, Check, Copy, Loader2, Paperclip, Pencil, RefreshCw, Square, ThumbsDown, ThumbsUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { AgentMarkdown } from './AgentMarkdown';
import { AnswerCards, normalizeCards } from './AnswerCards';
import { ActionCards, normalizeActions } from './ActionCards';
import { CitationNumbersContext, displayNumbers, normalizeSources } from './citations';
import { SourcesList } from './SourcesList';
import { StepTimeline, savedSteps } from './StepTimeline';
import { visibleMessages } from './turnState';
import type { AgentThreadController } from './useAgentThread';

export const THUMBS_UP_RATING = 10;
export const THUMBS_DOWN_RATING = 1;

export interface AgentFeedbackBridge {
  /** Latest rating per assistant message id (1–10 scale of the existing feedback endpoint). */
  ratings: Record<number, number | undefined>;
  submit: (messageId: number, rating: number) => void;
  pendingMessageId?: number | null;
}

export interface AgentMessageListProps {
  sessionId: number | null;
  messages: any[];
  controller: AgentThreadController;
  /** Any turn in flight (either engine): disables regenerate / edit / follow-ups. */
  busy: boolean;
  /** The server says a reply is pending but this tab has no live turn (e.g. after a refresh). */
  serverPending: boolean;
  userAvatar: React.ReactNode;
  /** Messages from the existing engine (slash commands, external mode, older threads). */
  renderLegacyMessage: (message: any) => React.ReactNode;
  renderAttachments?: (message: any) => React.ReactNode;
  feedback?: AgentFeedbackBridge;
  onFollowUp: (text: string) => void;
  streamingRef?: React.Ref<HTMLDivElement>;
}

const isAgentMessage = (m: any) => m?.metadata?.engine === 'agentcore';
const numericId = (m: any): number | null => (typeof m?.id === 'number' && m.id > 0 ? m.id : null);

function IconButton({ label, onClick, children, pressed, disabled }: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  pressed?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      className={`rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 dark:hover:bg-gray-700 dark:hover:text-gray-200 ${
        pressed ? 'text-orange-600 dark:text-orange-400' : ''
      }`}
    >
      {children}
    </button>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <IconButton
      label={copied ? 'Copied' : 'Copy response'}
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
    </IconButton>
  );
}

function UserMessage({ message, avatar, canEdit, onEdit }: {
  message: any;
  avatar: React.ReactNode;
  canEdit: boolean;
  onEdit: (content: string) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  if (draft !== null) {
    const submit = () => {
      const text = draft.trim();
      if (!text) return;
      setDraft(null);
      if (text !== message.content.trim()) onEdit(text);
    };
    return (
      <div className="relative mb-6 ml-[20%] rounded-lg bg-gray-100 p-4 dark:bg-gray-700">
        <Textarea
          autoFocus
          value={draft}
          aria-label="Edit message"
          className="min-h-[80px] bg-white dark:bg-gray-800"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            } else if (e.key === 'Escape') setDraft(null);
          }}
        />
        <div className="mt-2 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => setDraft(null)}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={!draft.trim()}>
            Send
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="group relative mb-6 ml-[20%] rounded-lg bg-gray-100 p-4 dark:bg-gray-700">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full">{avatar}</div>
        <div className="min-w-0 flex-1">
          <div className="whitespace-pre-wrap break-words text-foreground">{message.content}</div>
          <AttachmentChips items={message.metadata?.attachments} />
        </div>
      </div>
      {canEdit && (
        <div className="absolute right-2 top-2 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          <IconButton label="Edit and resend" onClick={() => setDraft(message.content)}>
            <Pencil className="h-3.5 w-3.5" />
          </IconButton>
        </div>
      )}
    </div>
  );
}

/** Files attached to a message (their text went to AIVIA; the files themselves were not stored). */
function AttachmentChips({ items }: { items: unknown }) {
  const names = Array.isArray(items) ? items.map((a: any) => a?.name).filter((n): n is string => typeof n === 'string') : [];
  if (!names.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" aria-label="Attachments">
      {names.map((name, i) => (
        <span key={`${name}-${i}`} className="inline-flex max-w-[220px] items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300">
          <Paperclip className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="truncate" title={name}>{name}</span>
        </span>
      ))}
    </div>
  );
}

function FollowUps({ items, onPick }: { items: string[]; onPick: (text: string) => void }) {
  if (!items.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-2" aria-label="Suggested follow-ups">
      {items.map((text) => (
        <button
          key={text}
          type="button"
          onClick={() => onPick(text)}
          className="rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-left text-xs text-orange-800 hover:bg-orange-100 dark:border-orange-800 dark:bg-orange-900/30 dark:text-orange-200 dark:hover:bg-orange-900/50"
        >
          {text}
        </button>
      ))}
    </div>
  );
}

function AssistantMessage({ message, isLast, busy, props }: {
  message: any;
  isLast: boolean;
  busy: boolean;
  props: AgentMessageListProps;
}) {
  const { feedback, sessionId, controller } = props;
  const sources = useMemo(() => normalizeSources(message.metadata?.sources), [message.metadata?.sources]);
  const cards = useMemo(() => normalizeCards(message.metadata?.cards), [message.metadata?.cards]);
  const actions = useMemo(() => normalizeActions(message.metadata?.actions), [message.metadata?.actions]);
  const steps = useMemo(() => savedSteps(message.metadata), [message.metadata]);
  const id = numericId(message);
  const rating = id !== null ? feedback?.ratings[id] : undefined;
  const status = message.metadata?.status;
  const followUps: string[] = Array.isArray(message.metadata?.followUps) ? message.metadata.followUps.filter((f: unknown) => typeof f === 'string') : [];

  return (
    <div className="relative mb-6 rounded-lg bg-white p-4 dark:bg-gray-800" data-message-id={message.id}>
      <StepTimeline steps={steps} />
      <AgentMarkdown content={message.content} sources={sources} onRevise={busy ? undefined : props.onFollowUp} />
      {status === 'stopped' && <p className="mt-2 text-xs italic text-gray-500">You stopped this answer.</p>}
      {status === 'failed' && <p className="mt-2 text-xs italic text-gray-500">This answer is incomplete.</p>}
      <ActionCards actions={actions} sessionId={sessionId} messageId={id} />
      <AnswerCards cards={cards} />
      {props.renderAttachments?.(message)}
      <SourcesList sources={sources} />
      <div className="mt-2 flex items-center gap-0.5">
        <CopyButton text={message.content} />
        {isLast && sessionId !== null && (
          <IconButton label="Regenerate response" disabled={busy} onClick={() => void controller.regenerate(sessionId)}>
            <RefreshCw className="h-4 w-4" />
          </IconButton>
        )}
        {feedback && id !== null && (
          <>
            <IconButton
              label="Good response"
              pressed={rating !== undefined && rating >= 6}
              disabled={feedback.pendingMessageId === id}
              onClick={() => feedback.submit(id, THUMBS_UP_RATING)}
            >
              <ThumbsUp className="h-4 w-4" />
            </IconButton>
            <IconButton
              label="Bad response"
              pressed={rating !== undefined && rating < 6}
              disabled={feedback.pendingMessageId === id}
              onClick={() => feedback.submit(id, THUMBS_DOWN_RATING)}
            >
              <ThumbsDown className="h-4 w-4" />
            </IconButton>
          </>
        )}
      </div>
      {isLast && !busy && <FollowUps items={followUps} onPick={props.onFollowUp} />}
    </div>
  );
}

function LiveAnswer({ props }: { props: AgentMessageListProps }) {
  const turn = props.controller.turn!;
  const sources = useMemo(() => normalizeSources(turn.done?.sources), [turn.done?.sources]);
  const cards = useMemo(() => normalizeCards(turn.done?.cards), [turn.done?.cards]);
  const streaming = turn.phase === 'streaming';
  return (
    <div ref={props.streamingRef} className="relative mb-6 rounded-lg bg-white p-4 dark:bg-gray-800" aria-busy={streaming}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <StepTimeline steps={turn.steps} live />
          {turn.content ? (
            <>
              <AgentMarkdown content={turn.content} sources={sources} streaming={streaming} />
              <AnswerCards cards={cards} />
            </>
          ) : (
            !turn.steps.some((s) => s.status === 'running') && (
              <div className="flex items-center gap-2 text-sm text-gray-500" aria-live="polite">
                <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
                <span>AIVIA is thinking…</span>
              </div>
            )
          )}
        </div>
        {streaming && (
          <Button variant="outline" size="sm" className="h-7 shrink-0 gap-1" onClick={props.controller.stop} aria-label="Stop generating">
            <Square className="h-3 w-3" /> Stop
          </Button>
        )}
      </div>
    </div>
  );
}

function ErrorCard({ props }: { props: AgentMessageListProps }) {
  const { turn, retry, dismissError } = props.controller;
  if (!turn?.error) return null;
  return (
    <div role="alert" className="mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <p className="flex-1">{turn.error.message}</p>
      <div className="flex shrink-0 gap-2">
        {turn.error.retryable && (
          <Button size="sm" variant="outline" className="h-7" disabled={props.busy} onClick={() => void retry()}>
            Retry
          </Button>
        )}
        <Button size="sm" variant="ghost" className="h-7" onClick={dismissError}>
          Dismiss
        </Button>
      </div>
    </div>
  );
}

/** Threads conversation for advisors on the AgentCore engine (flag `agentcoreThreads`). */
export function AgentMessageList(props: AgentMessageListProps) {
  const { messages, controller, sessionId, busy } = props;
  const turn = controller.turn && controller.turn.request.sessionId === sessionId ? controller.turn : null;
  const shown = useMemo(() => visibleMessages(messages ?? [], turn), [messages, turn]);
  const lastIndex = shown.length - 1;
  const liveAnswer = turn && turn.phase !== 'failed';
  const numbers = useMemo(
    () =>
      displayNumbers([
        ...shown
          .filter((m) => m.role === 'assistant' && isAgentMessage(m))
          .map((m) => ({ content: String(m.content ?? ''), sources: normalizeSources(m.metadata?.sources) })),
        { content: turn?.content ?? '', sources: normalizeSources(turn?.done?.sources) },
      ]),
    [shown, turn?.content, turn?.done?.sources],
  );

  return (
    <CitationNumbersContext.Provider value={numbers}>
      {shown.map((message, i) => {
        if (message.role === 'user') {
          const id = numericId(message);
          return (
            <UserMessage
              key={message.id}
              message={message}
              avatar={props.userAvatar}
              canEdit={id !== null && sessionId !== null && !busy}
              onEdit={(content) => void controller.edit(sessionId!, id!, content)}
            />
          );
        }
        if (message.role === 'assistant' && isAgentMessage(message)) {
          return <AssistantMessage key={message.id} message={message} isLast={i === lastIndex && !liveAnswer} busy={busy} props={props} />;
        }
        return <React.Fragment key={message.id}>{props.renderLegacyMessage(message)}</React.Fragment>;
      })}
      {liveAnswer ? (
        <LiveAnswer props={{ ...props, controller: { ...controller, turn } }} />
      ) : (
        props.serverPending &&
        !turn && (
          <div ref={props.streamingRef} className="mb-6 flex items-center gap-2 rounded-lg bg-white p-4 text-sm text-gray-500 dark:bg-gray-800">
            <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
            <span>AIVIA is working on this…</span>
          </div>
        )
      )}
      {turn && <ErrorCard props={{ ...props, controller: { ...controller, turn } }} />}
    </CitationNumbersContext.Provider>
  );
}
